import { forwardRef, useId } from 'react'
import type { ReactNode } from 'react'
import { cx } from './cx'

export interface SwitchProps {
  readonly id?: string
  readonly checked: boolean
  readonly onChange: (checked: boolean) => void
  readonly label: ReactNode
  /** Visually hide the label (still exposed to assistive tech) for compact
   * inline toggles. Never drop it: a switch with no accessible name is the
   * a11y review comment this primitive prevents. */
  readonly hideLabel?: boolean
  readonly disabled?: boolean
  readonly required?: boolean
  readonly className?: string
}

/**
 * Binary on/off toggle primitive (UID-229). A native `<input type="checkbox">`
 * with `role="switch"` — so it is operable by keyboard (Space) and announced
 * as a switch with its on/off state by screen readers for free, rather than a
 * bare styled `<div>`. The track/thumb are drawn in CSS from the input's
 * `:checked` state (ui.css `.switch-*`).
 *
 * A switch differs from a Checkbox in semantics: it takes effect immediately
 * (no separate submit), which is why it is its own primitive. Forwards its ref
 * to the underlying `<input>`.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { id, checked, onChange, label, hideLabel = false, disabled, required, className },
  ref,
) {
  const generatedId = useId()
  const switchId = id ?? generatedId

  return (
    <div className={cx('switch-field', className)}>
      <label htmlFor={switchId} className="switch-label">
        <input
          ref={ref}
          id={switchId}
          type="checkbox"
          role="switch"
          className="switch-input"
          checked={checked}
          disabled={disabled}
          // Native checkbox already exposes checked state, but role="switch"
          // requires an explicit aria-checked (biome a11y/useAriaPropsForRole);
          // keep it in sync with `checked`.
          aria-checked={checked}
          aria-required={required || undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
        <span className={hideLabel ? 'sr-only' : 'switch-label-text'}>{label}</span>
      </label>
    </div>
  )
})
